import clsx from 'clsx';
import React from 'react';

import { textMdSemibold, textXs } from '../../typography';
import { Radio } from '../Radio';

import * as styles from './index.module.css';

import type { RadioProps } from '../Radio';

type ItemProps = Omit<RadioProps, 'onChange' | 'onBlur' | 'name'> & {
  label: string | JSX.Element;
  element?: JSX.Element;
};

type Props = Omit<React.ComponentPropsWithoutRef<'div'>, 'onChange' | 'onBlur'> & {
  onChange: RadioProps['onChange'];
  onBlur: RadioProps['onBlur'];
  name: string;
  items: ItemProps[];
  error?: string;
  contentClassName?: string;
};

// https://www.figma.com/file/ssGmau7DMZsdIX7F6k0lGk/%E2%9A%A1%EF%B8%8F-Design-System?type=design&node-id=8-1579&mode=design&t=ofKIuOSPHQWXBcWo-0
export const RadioCard = React.forwardRef(
  ({ onChange, onBlur, name, items, error, contentClassName, ...props }: Props, ref: React.Ref<HTMLInputElement>) => {
    const idWithFallback = props.id ?? name;
    const errorId = `${idWithFallback}Error`;

    return (
      <div {...props}>
        {items.map(({ label, element, ...inputProps }) => (
          <div key={inputProps.value} className={clsx(styles.option, Boolean(error) && styles.error)}>
            <Radio
              {...inputProps}
              onChange={onChange}
              onBlur={onBlur}
              name={name}
              nativeInputClassName={styles.nativeInput}
              className={styles.input}
              ref={ref}
              aria-invalid={error ? 'true' : undefined}
              aria-describedby={error ? errorId : undefined}
            />
            <label className={styles.label} htmlFor={inputProps.id || `${name}-${inputProps.value}`}>
              <span className={clsx(textMdSemibold, styles.title)}>{label}</span>
            </label>
            <div className={clsx(styles.component, contentClassName)}>{element}</div>
          </div>
        ))}
        {Boolean(error) && (
          <div className={clsx(textXs, styles.errorMessage)} id={errorId} role='alert'>
            {error}
          </div>
        )}
      </div>
    );
  },
);
